import { type AddressComponent, type AddressTypeDto, PlaceType } from '@generated/search-bff';

import { type GenericSearchListing, type GenericExposeListing } from './searchResults';
import { type WithRequired } from '../types';

export const SUPPORTED_PLACE_TYPE_VALUES = [
  PlaceType.neighborhood,
  PlaceType.sublocality,
  PlaceType.postal_code,
  PlaceType.locality,
  PlaceType.administrative_area_level_4,
  PlaceType.administrative_area_level_1,
  PlaceType.country,
  PlaceType.natural_feature,
] as const satisfies readonly AddressTypeDto[];

export type SupportedPlaceType = (typeof SUPPORTED_PLACE_TYPE_VALUES)[number];

export const findLocalityPlaceId = (placeIds: string[]): string | undefined => {
  const localityPlaceId = placeIds.find((placeId) => placeId.includes('locality.'));
  if (localityPlaceId) {
    return localityPlaceId.split('.')[1];
  }
  // fallback to the highest administrative area in case there is no locality
  // Google Maps admin area levels go from 1 to 7, so a simple sort should do the trick to get the highest level.
  const orderedAdminAreas = placeIds.filter((placeIds) => placeIds.includes('administrative_area_level_'));

  orderedAdminAreas.sort();
  orderedAdminAreas.reverse();

  if (orderedAdminAreas.length > 0) {
    return orderedAdminAreas[0]?.split('.')[1];
  }

  const countryPlaceId = placeIds.find((placeId) => placeId.includes('country.'));

  return countryPlaceId?.split('.')[1];
};

export const SUPPORTED_PLACE_TYPES: ReadonlySet<SupportedPlaceType> = new Set(SUPPORTED_PLACE_TYPE_VALUES);

export const getFirstSupportedPlaceType = (types: PlaceType[]): SupportedPlaceType | undefined => {
  return types.find((type): type is SupportedPlaceType => SUPPORTED_PLACE_TYPES.has(type as SupportedPlaceType));
};
export type LocationBreadcrumbItem = {
  autogenUrl?: string;
  text: string;
  placeId?: string;
};

export const getLocationBreadcrumbString = ({
  listing,
  returnAllItems = false,
}: {
  listing: GenericExposeListing | GenericSearchListing;
  returnAllItems?: boolean;
}): string => {
  let breadcrumbArray = getLocationBreadcrumbArray({ listing }).reverse();
  if (breadcrumbArray.length === 0) {
    return '';
  }
  breadcrumbArray = returnAllItems ? breadcrumbArray : breadcrumbArray.slice(0, 2);
  return breadcrumbArray.map(({ text }) => text).join(', ');
};

const getLocationBreadcrumbText = ({
  listing,
  text,
  isSmallestAddressComponentLevel,
}: {
  listing: GenericExposeListing | GenericSearchListing;
  text: string;
  isSmallestAddressComponentLevel: boolean;
}) => {
  if (isSmallestAddressComponentLevel && 'neighborhoodOverwrite' in listing && listing.neighborhoodOverwrite) {
    return listing.neighborhoodOverwrite;
  }

  return text;
};

export function getLocationBreadcrumbArray({
  listing,
  shouldIncludeUrls,
}: {
  listing: GenericExposeListing | GenericSearchListing;
  shouldIncludeUrls?: boolean;
}): LocationBreadcrumbItem[] {
  // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const addressComponents = listing.addressComponents ?? [];
  const componentsWithText = addressComponents.filter((comp): comp is WithRequired<AddressComponent, 'text'> => Boolean(comp.text));

  return componentsWithText.reduce<LocationBreadcrumbItem[]>((result, { text, placeId, autogenUrl }, index, arr) => {
    const breadcrumbText = getLocationBreadcrumbText({
      listing,
      text,
      isSmallestAddressComponentLevel: index === arr.length - 1,
    });

    result.push({
      text: breadcrumbText,
      placeId,
      ...(shouldIncludeUrls && autogenUrl && { autogenUrl }),
    });

    return result;
  }, []);
}

export const getLastBreadcrumbString = (listing: GenericExposeListing | GenericSearchListing): string | undefined => {
  const breadcrumbArray = getLocationBreadcrumbArray({ listing });
  return breadcrumbArray.at(-1)?.text;
};
